import type { ReactNode } from 'react'
import store from 'state/knovStore'
import { isMobile } from 'lib/device'

type ConfirmExtraAction = {
    label: string
    onClick: () => void | Promise<void>
    variant?: 'primary' | 'secondary'
}

/**
 * Non-blocking confirm dialog that mimics the browser's native confirm API
 * Uses the existing modal system and Confirm component
 *
 * @param {ReactNode} message - The message to display in the confirm dialog
 * @param {Object} options - Optional configuration
 * @param {string} options.confirmLabel - Custom label for the confirm button (default: "OK")
 * @param {boolean} options.hideCancel - Whether to hide the cancel button (default: false)
 * @param {string | null} options.iconType - Optional icon type for the confirm dialog
 * @returns {Promise<boolean>} - Resolves to true if confirmed, false if canceled
 */
export const confirmAsync = (
    message: ReactNode,
    options: {
        confirmLabel?: string
        cancelMessage?: string
        hideCancel?: boolean
        iconType?: string | null
        extraActions?: ConfirmExtraAction[]
    } = {},
): Promise<boolean> => {
    return new Promise(resolve => {
        const { openConfirmModal } = store.getState().actions.modalActions

        const onConfirmCallback = () => resolve(true)
        const onCancelCallback = () => resolve(false)

        const extraActions =
            options.extraActions?.map(action => ({
                label: action.label,
                variant: action.variant,
                onClick: async () => {
                    // Treat "extra" actions as a cancellation of the primary confirm flow.
                    resolve(false)
                    await action.onClick()
                },
            })) || []

        const modalOptionsAndCallbacks = {
            onConfirm: onConfirmCallback,
            onCancel: onCancelCallback,
            hideCancel: options.hideCancel,
            iconType: options.iconType,
            cancelMessage: options.cancelMessage,
            extraActions,
        }

        openConfirmModal(
            message,
            options.confirmLabel || 'OK',
            onConfirmCallback,
            modalOptionsAndCallbacks,
        )
    })
}
